import ReactDOM from "react-dom/client";
import { useEffect, useRef, useState } from 'react';
import EventEmitter from "eventemitter3";
import FileField, { Callbacks, Translations } from "./upload/file_field";
import BaseUpload, { InitialTusFile } from "./upload/base_upload";
import { UPLOAD_ENDPOINT } from './api';
import { IFile } from './models';
import { apiGet } from './api';
import { RepositoryBrowserModal } from "./RepositoryBrowser";

export interface Options {
    uploadUrl?: string,
    callbacks?: Callbacks;
    chunkSize?: number;
    eventEmitter?: EventEmitter;
    prefix?: string;
    retryDelays?: number[];
    skipRequired?: boolean;
    supportDropArea?: boolean;
}

const findAncestor = (el: Element, name: string): Element => {
    while ((el = el.parentElement!) && (el.nodeName !== name));
    return el;
}

const fileGuid = (file: BaseUpload): string => {
    return file.getUrl()!.split('/').at(-1)!
}

export const initUploadField = (container: Element, files: Array<IFile>, options: Options = {}, canChange: boolean, multiple: boolean = false, keepOnServer: boolean = false): FileField | null => {

    if (container.querySelector("div.dff-files")) {
        return null; // ALready created
    }

    const form = findAncestor(container, "FORM");

    const uploadUrl = UPLOAD_ENDPOINT;
    const skipRequired = options.skipRequired || false;
    const prefix = null;

    let csrfToken = window.csrftoken || ""

    const input = container.querySelector(
        "input[type=file]:not([data-custom-upload])"
    ) as HTMLInputElement;


    const fieldName = input.name;

    const translations: Translations = {
        "Cancel": "Отменить",
        "Delete": "Удалить",
        "Delete failed": "Ошибка удаления",
        "Upload failed": "Ошибка загрузки",
        "Drop your files here": "Выберите файл или перетащите его сюда",
        "Invalid file type": "Неверный тип файла",
    }

    const supportDropArea = !(options.supportDropArea === false);

    const initial = files.map(file => ({
        type: "tus",
        id: file.guid,
        name: file.name,
        size: file.size,
        url: UPLOAD_ENDPOINT + file.guid,
    } as InitialTusFile))

    return new FileField({
        callbacks: options.callbacks || {},
        chunkSize: options.chunkSize || 2621440,
        csrfToken,
        eventEmitter: options.eventEmitter,
        fieldName,
        form,
        initial,
        input,
        multiple,
        parent: container,
        prefix,
        retryDelays: options.retryDelays || null,
        skipRequired,
        supportDropArea,
        translations,
        uploadUrl,
        canChange: canChange,
        keepOnServer: keepOnServer,
    });
};

export const initUploadFields = () => {

    var uploads = document.querySelectorAll("input[type=file]")

    for (let i = 0; i < uploads.length; i++) {
        const upload: HTMLInputElement = uploads[i] as HTMLInputElement;

        const name = upload.getAttribute("name") || "upload";
        const required = (upload.getAttribute("required") || "false") !== "false";
        const repository = upload.getAttribute("data-repository") !== "false";
        const multiple = upload.getAttribute("data-multiple") === "true";
        const initialData = upload.getAttribute("data-initial");

        let files: IFile[] = [];

        if (initialData) {
            try {
                const parsed = JSON.parse(initialData);
                if (Array.isArray(parsed)) {
                    files = parsed.map((file, index) => ({
                        id: index,
                        name: file.filename,
                        guid: file.guid,
                        size: file.size,
                    }));
                }
            } catch (e) {
                console.warn("Failed to parse data-initial:", e);
            }
        }

        const onSuccess = (name: string, file: IFile) => {
            var event = new Event('change');
            upload.dispatchEvent(event);
        }

        const onDelete = () => {
            var event = new Event('change');
            upload.dispatchEvent(event);
        }

        const holder = document.createElement("div");
        upload.replaceWith(holder);

        const customUpload = upload.hasAttribute("data-custom-upload");
        ReactDOM.createRoot(holder).render(<UploadField name={name} label={name} files={files} canChange={true}
            onSuccess={onSuccess} onDelete={onDelete} required={required} repository={repository}
            customUpload={customUpload} multiple={multiple} />
        )
    }
}

interface IUploadFieldProps {
    name: string
    label: string
    uploadUrl?: string
    required?: boolean
    repository?: boolean
    files: Array<IFile>
    onSuccess: (name: string, file: IFile) => void
    onDelete: (name: string, guid: string) => void
    canChange: boolean
    customUpload?: boolean
    multiple?: boolean
    keepOnServer?: boolean
}

function UploadField({ name, label, files, required, repository, onSuccess, onDelete, canChange, customUpload = false, multiple = false, keepOnServer = false }: IUploadFieldProps) {

    const field = useRef<HTMLDivElement>(null);
    const processedGuids = useRef<Set<string>>(
        new Set(files.map((f: any) => f.guid))
    );

    const initGuid = (files && files.length && files[0].guid) || "";

    const [guid, setGuid] = useState<string>(initGuid);
    const [browse, setBrowse] = useState<boolean>(false);
    const [uploadField, setUploadFiled] = useState<FileField | undefined>();

    const onFileSuccess = (file: BaseUpload) => {
        const currentFileGuid = fileGuid(file);
        if (!processedGuids.current.has(currentFileGuid)) {
            processedGuids.current.add(currentFileGuid);
            setGuid(currentFileGuid);
        }
    }

    const onFileDelete = (file: BaseUpload) => {
        onDelete(name, file.name);
        processedGuids.current.delete(fileGuid(file));
        if (processedGuids.current.size !== 0) {
            const remaining_guids = Array.from(processedGuids.current);
            setGuid(remaining_guids[remaining_guids.length - 1])
        }
        else {
            setGuid("")
        }
        if (required) document.getElementById('dff-file-' + name)?.setAttribute("required", "true");
    }

    const onSelect = (file: IFile) => {
        setBrowse(false);
        if (uploadField) {
            if (!processedGuids.current.has(file.guid)) {
                processedGuids.current.add(file.guid);
            }
            uploadField.setFiles([
                { id: file.guid, type: "tus", name: file.name, size: file.size, url: UPLOAD_ENDPOINT + file.guid }
            ])
        }
        setGuid(file.guid);
    }
    const onInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (customUpload && e.target.files && e.target.files.length > 0) {
            setGuid(e.target.files[0].name);
        }
    }
    useEffect(() => {
        if (guid) {
            if (multiple && processedGuids.current.has(guid)) {
                return;
            }

            if (multiple) {
                processedGuids.current.add(guid);
            }

            apiGet<IFile>('/automation/api/file/', { guid }).then((resp) => {
                onSuccess(name, resp);
                uploadField?.setFiles([{
                    id: resp.guid,
                    type: "tus",
                    name: resp.name,
                    size: resp.size,
                    url: UPLOAD_ENDPOINT + resp.guid
                }]);
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [guid]);

    useEffect(() => {
        if (field.current && !customUpload) {
            const uf = initUploadField(
                field.current,
                files,
                { callbacks: { onSuccess: onFileSuccess, onDelete: onFileDelete } },
                canChange,
                multiple,
                keepOnServer
            );
            if (uf) setUploadFiled(uf);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [field.current])

    useEffect(() => {
        if (uploadField && uploadField.updateCanChange) {
            uploadField.updateCanChange(canChange);
        }
    }, [canChange, uploadField]);

    return (
        <div className={`rd-upload${!canChange ? ' rd-upload--disabled' : ''}`}>
            {browse && <RepositoryBrowserModal onClose={() => setBrowse(false)} onSelect={onSelect} />}
            <div ref={field} className="rd-upload__container">
                <div className="rd-upload__selectors">
                    <div className={`rd-upload__file-input${repository && !customUpload ? ' rd-upload__file-input--with-repo' : ''}`}>
                        <input disabled={!canChange} type="file" id={`dff-file-${name}`} name={customUpload ? name : `file-${name}`} placeholder={label} required={required} className="rd-upload__native-input" onChange={customUpload ? onInputChange : undefined} />
                        <label className="rd-upload__file-label">Выбрать файл</label>
                        <span className="rd-upload__browse">Выбрать</span>
                        {!customUpload && Array.from(processedGuids.current).map((guid: string) => (
                            <input
                                key={guid}
                                disabled={!canChange}
                                type="hidden"
                                name={name}
                                value={guid}
                            />
                        ))}
                    </div>
                    {repository && !customUpload && <button type="button" disabled={!canChange} className="rd-btn rd-btn--medium rd-btn--outline rd-btn--primary" onClick={() => setBrowse(true)}>Репозиторий</button>}
                </div>
                {customUpload && (
                    <div className="dff-files">
                        <div className="dff-file">Выбранный файл: {guid}</div>
                    </div>
                )}
            </div>
        </div>
    );
}

export default UploadField;
